<template>
    <label class="f fv ath-form-color" :for="uid">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-color-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <div class="dw-10 pr">
            <input novalidate
                ref="input"
                class="dw-10"
                @input="onInputChange"
                :value="value"
                type="text"
                :name="uid"
                :id="uid"
                :disabled="disabled"
                :placeholder="$options.filters.athT(placeholder)" />
            <button type="button"
                class="el-btn br-m el--s-s el--transparent ath-form-color-btn"
                :title="$options.filters.athT(uiPredefinedTitle)"
                :style="{'background-color': color}"
                :disabled="disabled"
                @click="onColorClick">
            </button>
        </div>
    </label>
</template>

<script>
import { guuid, isNeArray, isNeString, join } from '@aretheyhappy/utils';
import FormColorList    from './FormColorList.vue';

export default {
    name: 'mead-form-color',
    props: {
        /**
         * Label i18n key rendered above the color input. Only shown when truthy.
         */
        lbl                 : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity           : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc         : {type: Boolean, default: () => true},
        /**
         * Placeholder text (i18n key) shown in the hex text input.
         */
        placeholder         : {type: [String, Boolean], default: () => 'mead.atom_form_color.placeholder'},
        /**
         * Currently selected hex color value (v-model).
         */
        value               : {type: String, default: () => '#000000'},
        /**
         * Disables the text input and the color swatch button.
         */
        disabled            : {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional          : {type: Boolean, default: () => false},
        /**
         * Title/tooltip i18n key shown on the color swatch button that opens the predefined color popover.
         */
        uiPredefinedTitle   : {type: String, default: () => 'mead.atom_form_color.predefined_colors'},
        /**
         * List of predefined hex colors offered in the color popover.
         */
        uiPredefinedList    : {type: Array, default: () => [
            //  1st row
            '#000000',
            '#666666',
            '#999999',
            '#bbbbbb',
            '#ffffff',
            //  2nd row
            '#ed635d',
            '#ee72c1',
            '#be74e0',
            '#825af6',
            '#5474f6',
            //  3rd row
            '#80dfe4',
            '#94d669',
            '#cee077',
            '#fbde6f',
            '#f7bf6a',
        ]},
    },
    data () {
        return {
            uid     : guuid(),
            color   : isNeArray(this.uiPredefinedList) ? this.uiPredefinedList[0] : '#000000',
            list    : this.uiPredefinedList,
        };
    },
    methods: {
        //  Return true if a valid hex color
        isValidHex: function (val) {
            if (!isNeString(val)) return false;
            return /^#+([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/g.test(val);
        },
        //  Patches the model and shown color
        patchModel: function (val) {
            //  If not valid hex color return
            if (!this.isValidHex(val)) return;

            //  Patch model
            this.color = val;

            //  Emit input event
            this.$emit('input', val);
        },
        //  Triggered when manual text entry happens in input ref
        onInputChange: function () {
            this.patchModel(this.$refs.input.value);
        },
        //  Triggered when someone clicks the color button
        onColorClick: function (event) {
            if (this.disabled) return;
            MeadCommon.PopoverService.open({comp: FormColorList}, event, {pubkey: this.uid, list: this.list});
        },
        //  Set color selected in the color list popover
        onColorSelect: function (obj) {
            if (!this.isValidHex(obj.selected)) return;
            this.patchModel(obj.selected);
        },
    },
    computed: {
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
    mounted () {
        //  If value exists, set color
        if (this.isValidHex(this.value)) this.color = this.value;

        //  Listen to colorpicked event from color list
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}:colorpicked`, this.onColorSelect);
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

$button_offset: .4rem;
$base_height: 3rem;

input {
    font-family: $ff-b;
    font-weight: 400;
    font-size: $fs-s;
    border: none;
    border-radius: $br-m;
    order: 1;
    height: $base_height;
    line-height: initial;
    padding: 0 ($base_height + $s-s) 0 $s-s;
    box-shadow: inset 0 0 4rem 4rem var(--c-brand-light-bg);
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);

    &[disabled="disabled"] {
        cursor: not-allowed;
        opacity: .65;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-internal-autofill-selected {
        color: var(--c-brand-light-fg) !important;
        -webkit-text-fill-color: var(--c-brand-light-fg) !important;
    }

    &:active,
    &:focus {
        border-color: transparent;
    }
}

.ath-form-color-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
    order: 0;
}

.ath-form-color-btn {
    position: absolute;
    bottom: $button_offset;
    right: $button_offset;
    width: $base_height - ($button_offset*2);
    height: $base_height - ($button_offset*2);
    padding: 0;
    z-index: 1;
}

.ath-form-color {
    &.el--invalid input {
        outline: 1px solid var(--c-brand-danger-bg);
    }
}

//  SIZES

@mixin inputSize ($name, $height, $icon_height) {
    .ath-form-color.el--s-#{$name} {
        input {
            height: $height;
            padding: 0 calc((($height - $icon_height)/2) + $s-s) 0 $s-s;
        }

        .ath-form-color-btn {
            bottom: calc(($height - $icon_height)/2);
            right: calc(($height - $icon_height)/2);
            height: $icon_height;
            width: $icon_height;
        }
    }
}

@include inputSize('s', 3rem, 2.2rem);
@include inputSize('m', 4rem, 2.6rem);
@include inputSize('l', 5rem, 3rem);
</style>
